import { useEffect, useMemo, useRef, useState } from "react";
import type { Texture } from "@textureninja/shared";
import { Thumbnail } from "./Thumbnail.js";

interface Props {
  textures: Texture[];
  onOpen: (t: Texture) => void;
  maxRows?: number;
  rowHeight?: number;
  gutter?: number;
}

interface Row {
  startIndex: number;
  numColumns: number;
  rowHeight: number;
  ratio: number;
  remainder: number;
}

function computeRows(
  textures: Texture[],
  width: number,
  defaultHeight: number,
  gutter: number,
  maxRows?: number,
): Row[] {
  if (!textures.length || width === 0) return [];
  const heightRatio = defaultHeight / 300;
  const rows: Row[] = [];
  let startIndex = 0;

  while (startIndex < textures.length) {
    let numColumns = 0;
    let currentWidth = 0;
    for (let i = startIndex; i < textures.length; i++) {
      const t = textures[i];
      if (!t) break;
      const textureWidth = t.width * heightRatio;
      if (currentWidth + textureWidth > width && i > startIndex) break;
      currentWidth += textureWidth;
      numColumns += 1;
    }
    if (numColumns === 0) break;
    currentWidth += gutter * (numColumns - 1);
    const ratio = width / currentWidth;
    const rowHeight = Math.floor(defaultHeight * ratio);
    let rowWidth = 0;
    for (let i = 0; i < numColumns; i++) {
      const t = textures[startIndex + i];
      if (!t) continue;
      rowWidth += Math.floor(t.width * ratio * heightRatio);
    }
    rowWidth += gutter * (numColumns - 1);
    rows.push({ startIndex, numColumns, rowHeight, ratio, remainder: width - rowWidth });
    startIndex += numColumns;
    if (maxRows && rows.length >= maxRows) break;
  }

  const last = rows[rows.length - 1];
  if (last && !maxRows) {
    last.rowHeight = defaultHeight;
    last.ratio = 1;
  }
  return rows;
}

export function Gallery({ textures, onOpen, maxRows, rowHeight = 220, gutter = 6 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rows = useMemo(
    () => computeRows(textures, width, rowHeight, gutter, maxRows),
    [textures, width, rowHeight, gutter, maxRows],
  );

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {rows.map((row, rowIdx) => {
        const heightRatio = rowHeight / 300;
        return (
          <div
            key={rowIdx}
            style={{
              display: "flex",
              gap: gutter,
              marginBottom: gutter,
              height: row.rowHeight,
            }}
          >
            {textures.slice(row.startIndex, row.startIndex + row.numColumns).map((texture, i) => {
              const isLast = i === row.numColumns - 1;
              const w =
                Math.floor(texture.width * row.ratio * heightRatio) + (isLast ? row.remainder : 0);
              const h = row.rowHeight;
              return (
                <Thumbnail
                  key={texture.id}
                  texture={texture}
                  width={Math.max(1, w)}
                  height={h}
                  onOpen={onOpen}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
